import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import FeedbackRead from "../../assets/icons/FeedbackRead.svg";
import FeedbackUnread from "../../assets/icons/FeedbackUnread.svg";
import { FeedbackData, getFeedbackData } from "../../Admin-Api/Feedback";
import { markFeedbackAsRead } from "../../Admin-Api/MarkFeedbackAsReadAsync";
import Footer from "../Footer/Footer";
import { deleteFeedbackAdmin } from "../../Admin-Api/FeedbackDeleteAdmin";

interface CustomJwtPayload {
  RoleId: string;
}

export const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const formatDate = (dateStr: string) => {
  const date = parseLocalDate(dateStr);
  return date ? date.toLocaleDateString("en-GB") : "";
};

const FeedbackSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
      </div>
    </div>

    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
      <div className="hidden lg:grid bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,2fr)_minmax(0,1fr)_auto] gap-3 border-b">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-5 bg-gray-200 rounded"></div>
        ))}
      </div>

      <div className="p-4 space-y-4 lg:hidden bg-gray-50">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="space-y-2 flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-4 w-40 bg-gray-200 rounded"></div>
              </div>
              <div className="h-5 w-5 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-2">
              {[...Array(4)].map((_, j) => (
                <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ViewFeedBack: React.FC = () => {
  const navigate = useNavigate();

  const [allFeedbackData, setAllFeedbackData] = useState<FeedbackData[]>([]);
  const [feedbackData, setFeedbackData] = useState<FeedbackData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const lastToastTimeRef = useRef<number>(0);
  const TOAST_THROTTLE_MS = 3000;
  const [readStatus, setReadStatus] = useState<Map<number, boolean>>(new Map());

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!startDate && !endDate) {
      setFeedbackData(allFeedbackData);
    }
  }, [startDate, endDate, allFeedbackData]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") {
          toast.error("Unauthorized access: RoleId is not 1");
          navigate("/");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Invalid token. Please log in again.");
        navigate("/");
      }
    }
  }, [navigate]);

  const handleSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const allIds = feedbackData.map(item => item.id);

    const isAllSelected = allIds.every(id => selectedIds.includes(id));

    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      setIsDeleting(true);

      const token = localStorage.getItem("token") || "";

      let successCount = 0;
      let failCount = 0;

      for (const id of selectedIds) {
        try {
          await deleteFeedbackAdmin(id, token);
          successCount++;
        } catch {
          failCount++;
        }
      }

      toast.success(`Feedback deleted successfully`);
      if (failCount > 0) toast.warning(`Failed to delete feedback`);

      setSelectedIds([]);
      setShowDeleteConfirm(false);

      await fetchFeedbackData();

    } catch {
      toast.error("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchFeedbackData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const data = await getFeedbackData(token);

      const sortedData = [...data].sort((a, b) => {
        if (a.isFlagged !== b.isFlagged) {
          return Number(a.isFlagged) - Number(b.isFlagged);
        }
        return (
          (parseLocalDate(b.createdDate)?.getTime() || 0) -
          (parseLocalDate(a.createdDate)?.getTime() || 0)
        );
      });

      setAllFeedbackData(sortedData);
      setFeedbackData(sortedData);

      const initialReadStatus = new Map<number, boolean>();
      sortedData.forEach((feedback) => {
        initialReadStatus.set(feedback.id, feedback.isFlagged);
      });
      setReadStatus(initialReadStatus);

    } catch (error) {
      console.error("Error fetching feedback data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFeedbackByDateRange = async () => {

    if (!startDate && !endDate) {
      setFeedbackData(allFeedbackData);
      return;
    }

    if (!startDate || !endDate) {
      setFeedbackData(allFeedbackData);

      if (!toast.isActive("select-dates")) {
        toast.info("Please select both start and end dates to filter.", {
          toastId: "select-dates",
        });
      }
      return;
    }

    setIsLoading(true);
    try {
      const start = parseLocalDate(startDate);
      const end = parseLocalDate(endDate);

      end?.setHours(23, 59, 59, 999);

      const filteredFeedbacks = allFeedbackData
        .filter((feedback) => {
          const created = parseLocalDate(feedback.createdDate);
          if (!created || !start || !end) return false;

          return created >= start && created <= end;
        })
        .sort((a, b) => Number(a.isFlagged) - Number(b.isFlagged));

      if (filteredFeedbacks.length === 0) {
        toast.info("No feedback found in selected date range.");
      }

      setFeedbackData(filteredFeedbacks);

    } catch (error) {
      console.error("Error filtering feedback:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbackData();
  }, []);

  const handleFeedbackClick = async (feedback: FeedbackData) => {
  try {
    // If not already read, mark as read
    if (!readStatus.get(feedback.id)) {
      const token = localStorage.getItem("token");

      if (token) {
        await markFeedbackAsRead(feedback.id, true, token);

        // Update local UI state immediately
        setReadStatus((prev) =>
          new Map(prev).set(feedback.id, true)
        );

        setFeedbackData((prevData) =>
          prevData.map((item) =>
            item.id === feedback.id
              ? { ...item, isFlagged: true }
              : item
          )
        );

        setAllFeedbackData((prevData) =>
          prevData.map((item) =>
            item.id === feedback.id
              ? { ...item, isFlagged: true }
              : item
          )
        );
      }
    }

    navigate(`/feedback-details/${feedback.id}`, {
      state: { feedbackData: feedback },
    });

  } catch (error) {
    console.error("Error updating read status:", error);

    navigate(`/feedback-details/${feedback.id}`, {
      state: { feedbackData: feedback },
    });
  }
};

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);

    if (endDate && endDate < newStartDate) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleMarkFeedbackAsRead = async (id: number, isFlagged: boolean) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      await markFeedbackAsRead(id, isFlagged, token);
      setReadStatus((prev) => new Map(prev).set(id, isFlagged));
      setFeedbackData((prevData) =>
        prevData.map((feedback) =>
          feedback.id === id ? { ...feedback, isFlagged } : feedback
        )
      );
      setAllFeedbackData((prevData) =>
        prevData.map((feedback) =>
          feedback.id === id ? { ...feedback, isFlagged } : feedback
        )
      );
    } catch (error) {
      console.error("Error marking feedback as read:", error);
    }
  };

  const totalRecords = feedbackData.length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex-grow">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
            <span className="bg-blue-600 w-2 h-8 rounded mr-2 sm:mr-3"></span>
            Manage Feedback
          </h1>
        </div>

        {isLoading ? (
          <FeedbackSkeleton />
        ) : (
          <>
            <div className="w-full mb-6">
              <div className="w-full bg-gray-100 rounded-xl px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-lg sm:text-xl text-gray-800 font-semibold">
                    Feedback Management
                  </h4>

                  {totalRecords > 0 && (
                    <span className="shrink-0 px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm font-medium">
                      {totalRecords} Records
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm mb-6">
              <div className="flex flex-wrap items-end justify-between gap-3">

                {/* LEFT SIDE */}
                <div className="flex flex-wrap items-end gap-3">

                  {/* Start Date */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      className="w-[140px] px-2 py-1.5 text-sm rounded-md border"
                    />
                  </div>

                  {/* End Date */}
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      className="w-[140px] px-2 py-1.5 text-sm rounded-md border"
                    />
                  </div>

                  {/* Search Button */}
                  <button
                    onClick={fetchFeedbackByDateRange}
                    className="h-[34px] px-3 bg-blue-600 text-white text-sm rounded-md"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>

                </div>

                {/* RIGHT SIDE */}
                <div className="ml-auto">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={selectedIds.length === 0}
                    className="h-[34px] px-3 bg-red-500 text-white text-sm rounded-md disabled:opacity-50"
                  >
                    Delete {selectedIds.length > 0 && `(${selectedIds.length})`} Selected
                  </button>
                </div>

              </div>
            </div>

            {totalRecords > 0 && (
              <>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
                  {/* Desktop Table */}
                  <div className="hidden lg:block w-full">
                    <div className="w-full">
                      <div className="bg-blue-500 p-4 grid grid-cols-[40px_1.2fr_1.7fr_1.2fr_1fr_1.3fr_2.5fr_0.8fr_60px] gap-3 border-b">
                        {/* SELECT ALL */}
                        <div className="flex items-start justify-center pt-1">
                          <input
                            type="checkbox"
                            checked={
                              feedbackData.length > 0 &&
                              selectedIds.length === feedbackData.length
                            }
                            onChange={handleSelectAll}
                          />
                        </div>

                        {[
                          "User Name",
                          "Email",
                          "Phone Number",
                          "Created Date",
                          "Subject",
                          "Feedback Content",
                          "OS",
                          "Action",
                        ].map((header) => (
                          <div
                            key={header}
                            className="flex items-start text-xs xl:text-sm font-semibold text-black leading-5"
                          >
                            <span className="w-full text-left">
                              {header}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="divide-y divide-gray-100">
                        {feedbackData.map((feedback) => {
                          const isRead = readStatus.get(feedback.id) || false;
                          return (
                            <div
                              key={feedback.id}
                              className="p-4 grid grid-cols-[40px_1.2fr_1.7fr_1.2fr_1fr_1.3fr_2.5fr_0.8fr_60px] gap-4 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150 items-start"
                              onClick={() => handleFeedbackClick(feedback)}
                            >

                              <div
                                className="flex justify-center items-start pt-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(feedback.id)}
                                  onChange={() => handleSelect(feedback.id)}
                                />
                              </div>
                              {[
                                feedback.userName,
                                feedback.email,
                                feedback.phoneNumber,
                                formatDate(feedback.createdDate),
                                feedback.subject || "No subject",
                                feedback.feedbackContent
                                  ? feedback.feedbackContent.length > 80
                                    ? `${feedback.feedbackContent.slice(0, 80)}...`
                                    : feedback.feedbackContent
                                  : "No content",
                                feedback.userOperatingSystem
                                  ? feedback.userOperatingSystem === 1
                                    ? "iOS"
                                    : "Android"
                                  : "web",
                              ].map((text, i) => (
                                <div
                                  key={i}
                                  className={`flex items-start min-w-0 ${isRead ? "text-gray-400" : "text-black"
                                    }`}
                                >
                                  <span className="w-full text-left text-xs xl:text-sm break-words whitespace-normal leading-6">
                                    {text}
                                  </span>
                                </div>
                              ))}

                              <div className="flex items-center justify-center pt-0.5">
                                <img
                                  src={isRead ? FeedbackRead : FeedbackUnread}
                                  alt={isRead ? "Mark as Unread" : "Mark as Read"}
                                  className="w-5 h-5 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkFeedbackAsRead(feedback.id, !isRead);
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="block lg:hidden p-4 space-y-4 bg-gray-50">
                    {feedbackData.map((feedback) => {
                      const isRead = readStatus.get(feedback.id) || false;
                      const osText = feedback.userOperatingSystem
                        ? feedback.userOperatingSystem === 1
                          ? "iOS"
                          : "Android"
                        : "web";

                      return (
                        <div
                          key={feedback.id}
                          onClick={() => handleFeedbackClick(feedback)}
                          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition"
                        >
                          <div className="flex items-start gap-3 mb-3">

                            <input
                              type="checkbox"
                              className="w-4 h-4 mt-1 accent-blue-600"
                              checked={selectedIds.includes(feedback.id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleSelect(feedback.id)}
                            />

                            {/* USER INFO */}
                            <div className="flex-1 min-w-0">
                              <h2
                                className={`text-sm sm:text-base font-bold break-words ${isRead ? "text-gray-400" : "text-gray-800"
                                  }`}
                              >
                                {feedback.userName}
                              </h2>
                              <p
                                className={`text-xs sm:text-sm break-all mt-1 ${isRead ? "text-gray-400" : "text-gray-600"
                                  }`}
                              >
                                {feedback.email}
                              </p>
                            </div>


                            <img
                              src={isRead ? FeedbackRead : FeedbackUnread}
                              alt="toggle read"
                              className="w-5 h-5 shrink-0 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkFeedbackAsRead(feedback.id, !isRead);
                              }}
                            />
                          </div>

                          <div className="space-y-2 text-sm">
                            <p className={isRead ? "text-gray-400" : "text-gray-700"}>
                              <span className="font-semibold text-gray-800">
                                Phone:
                              </span>{" "}
                              {feedback.phoneNumber}
                            </p>

                            <p className={isRead ? "text-gray-400" : "text-gray-700"}>
                              <span className="font-semibold text-gray-800">
                                Created Date:
                              </span>{" "}
                              {formatDate(feedback.createdDate)}
                            </p>

                            <p className={isRead ? "text-gray-400" : "text-gray-700"}>
                              <span className="font-semibold text-gray-800">
                                Subject:
                              </span>{" "}
                              {feedback.subject || "No subject"}
                            </p>

                            <p className={isRead ? "text-gray-400" : "text-gray-700"}>
                              <span className="font-semibold text-gray-800">
                                Feedback:
                              </span>{" "}
                              {feedback.feedbackContent
  ? feedback.feedbackContent.length > 80
                              ? `${feedback.feedbackContent.slice(0, 80)}...`
                              : feedback.feedbackContent
                              : "No content"}
                            </p>

                            <p className={isRead ? "text-gray-400" : "text-gray-700"}>
                              <span className="font-semibold text-gray-800">
                                OS:
                              </span>{" "}
                              {osText}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </>
            )}
          </>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-[400px]">

              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Delete Feedback
              </h2>

              <p className="text-sm text-gray-600 mb-5">
                Are you sure you want to delete {selectedIds.length} selected {selectedIds.length !== 1 ? "feedbacks" : "feedback"}?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedIds([]);
                  }}
                  disabled={isDeleting}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="text-sm px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting && (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}

                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          theme="light"
        />
      </div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-md p-3 flex items-center justify-between lg:hidden">

          <span className="text-sm font-medium text-gray-700">
            {selectedIds.length} selected
          </span>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-md text-sm hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      )}

      

      <Footer />
    </div>
  );
};

export default ViewFeedBack;